


































































































































































































































































import { Component, Vue } from 'vue-property-decorator'
import { Referral } from '@/api/model/referral'
import { isEqual } from 'lodash'
import { namespace } from 'vuex-class'
import { PipelinePhase } from '@/api/model/pipeline-phase'
import { PipelinePhaseCategory } from '@/api/types'
const referrals = namespace('referrals')

@Component
export default class ReferralList extends Vue {
    @referrals.State readonly loading!: boolean
    @referrals.State readonly referrals!: Referral[]
    @referrals.State readonly view!: string
    @referrals.State readonly direction!: string
    @referrals.State readonly orderField!: string|null
    @referrals.State readonly orderDirection!: string|null
    @referrals.State readonly totalPages!: number
    @referrals.State readonly page!: number
    @referrals.State readonly statusFilter!: string
    @referrals.State readonly dateSpecificFilter!: Date
    @referrals.State readonly dateRangeFilter!: string
    @referrals.State readonly pipelineFilter!: string
    @referrals.State readonly phaseFilter!: PipelinePhase
    @referrals.State readonly phaseCategoryFilter!: PipelinePhaseCategory
    @referrals.State readonly search!: string|null
    @referrals.State readonly itemsPerPage!: number
    @referrals.State readonly displayMode!: string
    @referrals.Action nextPage: () => Promise<void>
    @referrals.Action prevPage: () => Promise<void>
    @referrals.Action applyView: ({ view: ReferralView, direction: ReferralFilterDirection }) => Promise<void>
    @referrals.Action applyOrder: ({ field, direction }) => Promise<void>
    @referrals.Action applyDateRangeFilter: ({ range: string, update: boolean }) => Promise<void>
    @referrals.Action applyPipelineFilter: ({ id: string, update: boolean }) => Promise<void>
    @referrals.Action resetFilters: () => Promise<void>
    @referrals.Action applySearch: ({ query }) => Promise<void>
    @referrals.Action snapshot: () => Promise<void>
    @referrals.Action setItemsPerPage: ({ itemsPerPage: number }) => Promise<void>
    @referrals.Action setDisplayMode: ({ displayMode: string }) => Promise<void>
    @referrals.Getter activeFilterCount: number
    showingFilters: boolean = false
    searchTimeout: NodeJS.Timeout = null

    async created(): Promise<void> {
        // Set a default if no pipeline is set. (sets status to open)
        if (this.$currentCompany.pipelinesEnabled) {
            if (this.pipelineFilter === null && this.phaseFilter === null && this.phaseCategoryFilter === null) {
                await this.applyPipelineFilter({
                    id: null,
                    update: false,
                })
            }
        }

        // Set a default if no view is set.
        if (this.view === null) {
            await this.applyView({
                view: 'user',
                direction: 'in',
            })
        }

        // Snapshot currently set filters.
        await this.snapshot()

        this.showingFilters = this.activeFilterCount > 0
    }

    onDateRangeChanged(value: string): void {
        if (isEqual(this.dateRangeFilter, value)) {
            return
        }

        this.applyDateRangeFilter({
            range: value,
            update: true,
        })
    }

    onSearchChange(query: string) {
        clearTimeout(this.searchTimeout)
        this.searchTimeout = setTimeout(() => this.applySearch({ query }), 1000)
    }

    onItemsPerPageInput(itemsPerPage: number) {
        if (isEqual(this.itemsPerPage, itemsPerPage)) {
            return
        }

        this.setItemsPerPage({ itemsPerPage })
    }

    onDisplayModeInput(displayMode: number) {
        if (isEqual(this.displayMode, displayMode)) {
            return
        }

        this.setDisplayMode({ displayMode })
    }

    changeOrder(field: string, direction: string) {
        if (this.orderField === field) {
            direction = this.orderDirection === 'asc' ? 'desc' : 'asc'

        } else {
            direction = 'desc'
        }

        this.applyOrder({
            field,
            direction,
        })
    }

    generateHeaderClasses(field: string) {
        return {
            'sortable': true,
            'sorted': this.orderField === field,
            'sorted--asc': this.orderField === field && this.orderDirection === 'asc',
            'sorted--desc': this.orderField === field && this.orderDirection === 'desc',
        }
    }

    toggleFilters(): void {
        this.showingFilters = !this.showingFilters
    }

    get itemsPerPageOptions(): Array<{ label: string, value: number }> {
        return [10, 25, 50, 75, 100].map(value => {
            return {
                label: value.toString(),
                value,
            }
        })
    }

    get displayModeOptions(): Array<{ label: string, value: string }> {
        return ['appointments', 'actions', 'valuation', 'createdAt'].map(value => {
            return {
                label: this.$t('referral-list.displayModes.' + value).toString(),
                value,
            }
        })
    }

    get dateRangeOptions() {
        return [
            { label: this.$t('referral-list.dateRange.all').toString(), value: 'all' },
            { label: this.$t('referral-list.dateRange.week').toString(), value: 'week' },
            { label: this.$t('referral-list.dateRange.month').toString(), value: 'month' },
            { label: this.$t('referral-list.dateRange.year').toString(), value: 'year' },
            { label: this.$t('referral-list.dateRange.thisWeek').toString(), value: 'this-week' },
            { label: this.$t('referral-list.dateRange.thisMonth').toString(), value: 'this-month' },
            { label: this.$t('referral-list.dateRange.thisYear').toString(), value: 'this-year' },
        ]
    }

    get isLoading(): boolean {
        return this.loading
    }

    get hasResults(): boolean {
        return this.isLoading === false && this.referrals.length > 0
    }

    get hasNoResults(): boolean {
        return this.isLoading === false && this.referrals.length === 0
    }

    get componentClasses() {
        return {
            'referrals': true,
            'referrals-beta': this.$currentUser?.betaOptInEnabled,
            'referrals--with-pipeline': this.pipelineFilter === null,
            'referrals--without-pipeline': this.pipelineFilter !== null,
        }
    }
}
